import { useState, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  TextInput,
  Button,
  UnstyledButton,
  Modal,
  Paper,
  ScrollArea,
  Divider,
} from '@mantine/core';
import {
  IconMapPin,
  IconPlus,
  IconBuilding,
  IconDoor,
  IconChevronRight,
  IconSearch,
} from '@tabler/icons-react';
import { useSites, useCreateSite, formatSiteAddress } from '@/hooks/api/useSites';
import {
  useLocationsBySite,
  useCreateLocation,
} from '@/hooks/api/useLocations';
import { useOrganization } from '@/hooks/useOrganization';
import type { Site, Location } from '@/types/models';

interface HierarchicalLocationPickerProps {
  siteId: string | null;
  locationId: string | null;
  onSiteChange: (siteId: string | null) => void;
  onLocationChange: (locationId: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
}

export function HierarchicalLocationPicker({
  siteId,
  locationId,
  onSiteChange,
  onLocationChange,
  disabled = false,
  error,
}: HierarchicalLocationPickerProps) {
  const { organizationId } = useOrganization();
  const { data: sites = [] } = useSites(organizationId);
  const { data: subLocations = [] } = useLocationsBySite(organizationId, siteId);

  const createSite = useCreateSite();
  const createLocation = useCreateLocation();

  const [modalOpened, setModalOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedSiteInModal, setSelectedSiteInModal] = useState<string | null>(siteId);
  const [selectedLocationInModal, setSelectedLocationInModal] = useState<string | null>(locationId);
  const [showCreateLocation, setShowCreateLocation] = useState(false);
  const [showCreateSubLocation, setShowCreateSubLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [newSubLocationName, setNewSubLocationName] = useState('');

  // Get sub-locations for selected site in modal
  const { data: modalSubLocations = [] } = useLocationsBySite(organizationId, selectedSiteInModal);

  // Get selected site and location objects
  const selectedSite = useMemo(
    () => sites.find((s) => s.id === siteId) || null,
    [sites, siteId]
  );
  const selectedLocation = useMemo(
    () => subLocations.find((l) => l.id === locationId) || null,
    [subLocations, locationId]
  );

  // Filter sites based on search
  const filteredSites = useMemo(() => {
    if (!search) return sites;
    const searchLower = search.toLowerCase();
    return sites.filter(
      (site) =>
        site.name.toLowerCase().includes(searchLower) ||
        formatSiteAddress(site).toLowerCase().includes(searchLower)
    );
  }, [sites, search]);

  // Get display text for button
  const displayText = useMemo(() => {
    if (!selectedSite) return null;
    if (selectedLocation) {
      return `${selectedSite.name} > ${selectedLocation.name}`;
    }
    return selectedSite.name;
  }, [selectedSite, selectedLocation]);

  // Open modal and sync state
  const handleOpenModal = () => {
    setSelectedSiteInModal(siteId);
    setSelectedLocationInModal(locationId);
    setSearch('');
    setShowCreateLocation(false);
    setShowCreateSubLocation(false);
    setModalOpened(true);
  };

  // Handle site selection in modal
  const handleSelectSiteInModal = (site: Site) => {
    setSelectedSiteInModal(site.id);
    setSelectedLocationInModal(null);
    setShowCreateSubLocation(false);
  };

  // Handle sub-location selection in modal
  const handleSelectSubLocationInModal = (location: Location | null) => {
    setSelectedLocationInModal(location?.id || null);
  };

  // Confirm selection
  const handleConfirm = () => {
    onSiteChange(selectedSiteInModal);
    onLocationChange(selectedLocationInModal);
    setModalOpened(false);
  };

  // Clear selection
  const handleClear = () => {
    onSiteChange(null);
    onLocationChange(null);
    setSelectedSiteInModal(null);
    setSelectedLocationInModal(null);
    setModalOpened(false);
  };

  // Handle creating new location
  const handleCreateLocation = async () => {
    if (!organizationId || !newLocationName.trim()) return;

    const newSite = await createSite.mutateAsync({
      name: newLocationName.trim(),
      address: newLocationAddress.trim() || undefined,
      organization_id: organizationId,
    });

    setSelectedSiteInModal(newSite.id);
    setSelectedLocationInModal(null);
    setShowCreateLocation(false);
    setNewLocationName('');
    setNewLocationAddress('');
  };

  // Handle creating new sub-location
  const handleCreateSubLocation = async () => {
    if (!organizationId || !selectedSiteInModal || !newSubLocationName.trim()) return;

    const newLocation = await createLocation.mutateAsync({
      name: newSubLocationName.trim(),
      site_id: selectedSiteInModal,
      type: 'Room',
      status: 'Active',
      organization_id: organizationId,
    });

    setSelectedLocationInModal(newLocation.id);
    setShowCreateSubLocation(false);
    setNewSubLocationName('');
  };

  const selectedSiteInModalObj = sites.find((s) => s.id === selectedSiteInModal);

  return (
    <>
      {/* Main button trigger */}
      <UnstyledButton
        onClick={handleOpenModal}
        disabled={disabled}
        style={{
          display: 'block',
          width: '100%',
          padding: '0.625rem 0.75rem',
          border: '1px solid var(--mantine-color-gray-4)',
          borderRadius: 'var(--mantine-radius-sm)',
          backgroundColor: disabled ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-body)',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {displayText ? (
          <Group gap="xs" wrap="nowrap">
            <IconMapPin size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm" truncate style={{ flex: 1 }}>
              {displayText}
            </Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            Select location...
          </Text>
        )}
      </UnstyledButton>

      {error && (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      )}

      {/* Location Selection Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Select Location"
        size="md"
      >
        <Stack gap="md">
          {/* Instructions */}
          <Text size="sm" c="dimmed">
            Select a main location (e.g., office building) and optionally a sub-location (e.g., room, floor).
          </Text>

          {/* Search */}
          <TextInput
            placeholder="Search locations..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* Main Locations List */}
          <Box>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>Main Location</Text>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setShowCreateLocation(true)}
              >
                New Location
              </Button>
            </Group>

            {showCreateLocation ? (
              <Paper p="sm" withBorder>
                <Stack gap="sm">
                  <TextInput
                    label="Location Name"
                    placeholder="e.g., Maryland Office"
                    required
                    value={newLocationName}
                    onChange={(e) => setNewLocationName(e.target.value)}
                    autoFocus
                  />
                  <TextInput
                    label="Address (optional)"
                    placeholder="e.g., 123 Main St, Baltimore, MD"
                    value={newLocationAddress}
                    onChange={(e) => setNewLocationAddress(e.target.value)}
                  />
                  <Group justify="flex-end" gap="xs">
                    <Button variant="subtle" size="xs" onClick={() => setShowCreateLocation(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      onClick={handleCreateLocation}
                      loading={createSite.isPending}
                      disabled={!newLocationName.trim()}
                    >
                      Create
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            ) : (
              <ScrollArea.Autosize mah={200}>
                <Stack gap={4}>
                  {filteredSites.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No locations found. Create one to get started.
                    </Text>
                  ) : (
                    filteredSites.map((site) => (
                      <UnstyledButton
                        key={site.id}
                        onClick={() => handleSelectSiteInModal(site)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          borderRadius: 'var(--mantine-radius-sm)',
                          backgroundColor:
                            selectedSiteInModal === site.id
                              ? 'var(--mantine-color-blue-1)'
                              : 'transparent',
                          border:
                            selectedSiteInModal === site.id
                              ? '1px solid var(--mantine-color-blue-4)'
                              : '1px solid transparent',
                        }}
                      >
                        <Group gap="xs" wrap="nowrap">
                          <IconBuilding size={16} color="var(--mantine-color-gray-6)" />
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500}>
                              {site.name}
                            </Text>
                            {formatSiteAddress(site) && (
                              <Text size="xs" c="dimmed" truncate>
                                {formatSiteAddress(site)}
                              </Text>
                            )}
                          </Box>
                        </Group>
                      </UnstyledButton>
                    ))
                  )}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </Box>

          {/* Sub-Locations */}
          {selectedSiteInModal && (
            <>
              <Divider />
              <Box>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500}>Sub-Location (optional)</Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setShowCreateSubLocation(true)}
                  >
                    New Sub-Location
                  </Button>
                </Group>

                <Text size="xs" c="dimmed" mb="xs">
                  Adding to: <strong>{selectedSiteInModalObj?.name}</strong>
                </Text>

                {showCreateSubLocation ? (
                  <Paper p="sm" withBorder>
                    <Stack gap="sm">
                      <TextInput
                        label="Sub-Location Name"
                        placeholder="e.g., Room 101, Floor 2, Server Room"
                        required
                        value={newSubLocationName}
                        onChange={(e) => setNewSubLocationName(e.target.value)}
                        autoFocus
                      />
                      <Group justify="flex-end" gap="xs">
                        <Button variant="subtle" size="xs" onClick={() => setShowCreateSubLocation(false)}>
                          Cancel
                        </Button>
                        <Button
                          size="xs"
                          onClick={handleCreateSubLocation}
                          loading={createLocation.isPending}
                          disabled={!newSubLocationName.trim()}
                        >
                          Create
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>
                ) : (
                  <ScrollArea.Autosize mah={150}>
                    <Stack gap={4}>
                      {/* None option */}
                      <UnstyledButton
                        onClick={() => handleSelectSubLocationInModal(null)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          borderRadius: 'var(--mantine-radius-sm)',
                          backgroundColor:
                            selectedLocationInModal === null
                              ? 'var(--mantine-color-gray-1)'
                              : 'transparent',
                          border: '1px solid transparent',
                        }}
                      >
                        <Text size="sm" c="dimmed">None</Text>
                      </UnstyledButton>

                      {modalSubLocations.map((location) => (
                        <UnstyledButton
                          key={location.id}
                          onClick={() => handleSelectSubLocationInModal(location)}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            borderRadius: 'var(--mantine-radius-sm)',
                            backgroundColor:
                              selectedLocationInModal === location.id
                                ? 'var(--mantine-color-blue-1)'
                                : 'transparent',
                            border:
                              selectedLocationInModal === location.id
                                ? '1px solid var(--mantine-color-blue-4)'
                                : '1px solid transparent',
                          }}
                        >
                          <Group gap="xs" wrap="nowrap">
                            <IconDoor size={14} color="var(--mantine-color-gray-6)" />
                            <Text size="sm">{location.name}</Text>
                          </Group>
                        </UnstyledButton>
                      ))}
                    </Stack>
                  </ScrollArea.Autosize>
                )}
              </Box>
            </>
          )}

          {/* Current Selection Preview */}
          {selectedSiteInModal && (
            <>
              <Divider />
              <Paper p="sm" withBorder bg="blue.0">
                <Group gap="xs" wrap="nowrap">
                  <IconMapPin size={16} color="var(--mantine-color-blue-6)" />
                  <Text size="sm" fw={500}>
                    {selectedSiteInModalObj?.name}
                    {selectedLocationInModal && modalSubLocations.find((l) => l.id === selectedLocationInModal) && (
                      <>
                        <IconChevronRight
                          size={12}
                          style={{ verticalAlign: 'middle', margin: '0 4px' }}
                        />
                        {modalSubLocations.find((l) => l.id === selectedLocationInModal)?.name}
                      </>
                    )}
                  </Text>
                </Group>
              </Paper>
            </>
          )}

          {/* Actions */}
          <Group justify="space-between">
            <Button variant="subtle" color="red" onClick={handleClear}>
              Clear
            </Button>
            <Group gap="xs">
              <Button variant="subtle" onClick={() => setModalOpened(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirm}>
                Confirm
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
